import React, { useState } from 'react';
import AddAgencyOfficialDialogContent from './AddAgencyOfficialDialogContent';
import { useUser } from '../../../../hooks/useUser';
import { useAgency } from '../../../../hooks/useAgency';
import { addAgencyOfficial, OfficialData } from '../../../../networkRequest/officialsService';
import AddAgencyOfficialDialogActions from './AddAgencyOfficialDialogActions';
import { z as zod } from 'zod';
import { SelectChangeEvent } from '@mui/material';
import { AgencyOfficialRolesMap } from '../..';
import { FormOfficialData, OfficialFormHelpers } from '../../../../utils/officialFormHelpers';
import DOMPurify from 'dompurify';

interface AddAgencyOfficialDialogFormProps {
  onSuccess: () => void,
  onClose: () => void;
  onError: (error: string) => void;
  roles: AgencyOfficialRolesMap;
  padding: string;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setNewOfficial: React.Dispatch<React.SetStateAction<OfficialData | null>>;
}

export const formOfficialDataInit: FormOfficialData = {
  first_name: '',
  last_name: '',
  title: '',
  email: '',
  badge_number: '',
  role: '',
  rank: '',
  phone_number: '',
  armed_permission: false,
  employment_type: 'staff_member'
};

const AddAgencyOfficialDialogForm = ({
  onSuccess,
  onClose,
  onError,
  roles,
  padding,
  setActiveStep,
  setNewOfficial
}: AddAgencyOfficialDialogFormProps) => {
  const { user } = useUser();
  const { agency } = useAgency();

  const [formOfficialData, setFormOfficialData] = useState(formOfficialDataInit);
  const [errors, setErrors] = useState<zod.ZodError<any> | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!user) {
      throw new Error('User not found');
    }
    if (!agency) {
      throw new Error('Agency not set');
    }

    const validForm = OfficialFormHelpers.validateForm(formOfficialData, roles, false, setErrors, onError);
    if (!validForm) {
      setLoading(false);
      return;
    }

    const data = OfficialFormHelpers.formatOfficialFormData(formOfficialData, roles);
    if (!data) return;

    const nextStep = OfficialFormHelpers.isSecurityContractor(formOfficialData) ? 2 : 1;

    try {
      const response = await addAgencyOfficial(user.accessToken, agency.id, data);
      if (response.error) throw new Error(response.error);
      if (response && response.data) {
        setActiveStep(nextStep);
        setNewOfficial(response.data);
        onSuccess();
      }
    } catch (error: any) {
      onError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // must have separate handlers or a type guard to appease TS
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = DOMPurify.sanitize(value);
    setFormOfficialData({ ...formOfficialData, [name]: sanitizedValue });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    const sanitizedValue = DOMPurify.sanitize(value);
    const newFormOfficialData = { ...formOfficialData, [name]: sanitizedValue };
    // When the role is changed, if there are validation errors, re-run
    // validation. This is done to update the email field validation error
    // when it changes between required and optional
    if (name === 'role' && errors !== null) {
      OfficialFormHelpers.validateForm(newFormOfficialData, roles, false, setErrors, onError);
    }
    setFormOfficialData(newFormOfficialData);
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;
    setFormOfficialData({ ...formOfficialData, [name]: checked });
  };

  return (  
    <form onSubmit={handleSubmit} role='form'>
      <AddAgencyOfficialDialogContent
        handleChange={{ handleInputChange, handleSelectChange, handleSwitchChange }}
        formOfficialData={formOfficialData}
        roles={roles}
        formErrors={errors}
      />
      <AddAgencyOfficialDialogActions
        onClose={onClose}
        padding={padding}
        canContinue={true}
        loading={loading}
      />
    </form>
  );
};

export default AddAgencyOfficialDialogForm;
